/**
 * Single source of truth for SPA routes.
 *
 * Every hash route the app understands lives here. Three consumers read it:
 *   1) src/lib/constants.js exports `NAV_GROUPS` derived from this list, so the
 *      sidebar renders entries with a `group` field.
 *   2) src/console.raw.js expects a `registerRoute("<id>", handler)` for every
 *      `id` listed below; the build smoke check (scripts/smoke-routes.mjs)
 *      enforces that.
 *   3) Aliases (e.g. `#/alarm-log` → `signals`) live next to their canonical
 *      route so future modular splits cannot silently drop deep links.
 *
 * When you add a route:
 *   - Add an entry here.
 *   - Implement `registerRoute("<id>", handler)` in src/console.raw.js (or, in
 *     a future modular split, src/routes/<id>.js).
 *   - If it should appear in the sidebar, set `group` + `label` + `ico`.
 *   - If it can be reached by an unauthenticated user, set `public: true`.
 *   - If a different hash should resolve to the same handler, list it in
 *     `aliases` **and** add a row to `ROUTE_REDIRECT_REGISTRY` (ZD-PARTIAL-F18).
 */

/**
 * ZD-PARTIAL-F18 — every live SPA redirect / alias declares owner · reason · expireTip.
 * After ``expireTip`` is live on host, a follow-up wave deletes the alias/soft route
 * (do not renew forever). ``to`` is the final user-visible destination hash.
 *
 * @typedef {{ from: string, to: string, owner: string, reason: string, expireTip: number, kind: "alias"|"soft" }} RedirectSpec
 */
export const ROUTE_REDIRECT_REGISTRY = Object.freeze([
  // tip575 softs purged tip578: #/group/demo · bare #/group
  // tip580 aliases+softs purged tip581: overview-groups/ai/governance/alarm-log · events/activity/signals/audit
  // tip582 aliases purged tip582: incidents-legacy · city-observation
  // tip585 soft purged tip583: bare #/devices → #/group/_all (keep #/devices/:id · ?view=capabilities)
]);

/** @type {ReadonlyArray<RouteSpec>} */
export const ROUTES = Object.freeze([
  // -------------------------------------------------------------- public auth
  { id: "login", hash: "#/login", public: true },
  { id: "forgot-password", hash: "#/forgot-password", public: true },
  { id: "register", hash: "#/register", public: true },
  { id: "account-activate", hash: "#/account-activate", public: true },

  // -------------------------------------------------------------- account / settings hub
  // Account = personal hub (user menu only). Settings = admin/policy hub (nav + user menu).
  { id: "account", hash: "#/account", min: "user" },
  { id: "settings", hash: "#/settings", min: "user", group: "Administration", label: "Settings", ico: "⚙" },
  // Deep link / Settings — fleet catalog hub (CAM-S4). Discovery still on #/devices/:id.
  { id: "account-cameras", hash: "#/account/cameras", min: "admin" },

  // -------------------------------------------------------------- Home
  // ops-live: SA/staff platform-health ONLY (IA Platform · Health). Tenant map SoT = Home.
  {
    id: "ops-live",
    hash: "#/ops-live",
    min: "superadmin",
    saStaffNav: true,
    group: "Platform",
    label: "Health",
    ico: "▣",
  },
  /** Tenant Home — Spatial Context + App GPS people pins (SoT). */
  {
    id: "overview",
    hash: "#/overview",
    min: "user",
    group: "Home",
    label: "Home",
    ico: "◎",
  },
  {
    id: "readiness",
    hash: "#/readiness",
    min: "admin",
    group: "Operations",
    label: "Readiness",
    ico: "◈",
  },
  {
    id: "response-drill",
    hash: "#/response-drill",
    min: "admin",
    group: "Operations",
    label: "Response drill",
    ico: "◇",
    navCatalogId: "response_drill",
  },
  { id: "devices", hash: "#/devices", min: "user", group: "Assets", label: "All devices", ico: "▢", navPath: "#/group/_all" },
  /** SA site lookup — deep link only (CONV-1 hide-nav). */
  { id: "site", hash: "#/site", min: "superadmin" },
  // Deep link only (no nav entry): #/group/:key — #/group/_all is the fleet list.
  // Do NOT bind hash "#/group/_all" to id "devices" (stole URL → capabilities / snapshot UI).
  { id: "group", hash: "#/group", min: "user" },
  // #/groups — Group share hub (P1+P2 table). tip575 softs #/group|demo purged tip578.
  {
    id: "groups",
    hash: "#/groups",
    min: "user",
    group: "Assets",
    label: "Groups",
    ico: "⎔",
  },

  // -------------------------------------------------------------- capability centers (flag-gated)
  // ZD-DEAD: `#/intelligence` registry deleted (expire tip549 soak). AI = Home Context + `#/auto-sentinel`.
  {
    id: "auto-sentinel",
    hash: "#/auto-sentinel",
    min: "admin",
    group: "Administration",
    label: "AI Policy",
    ico: "◎",
  },
  {
    id: "automation",
    hash: "#/automation",
    min: "admin",
    group: "Operations",
    label: "Auto rules",
    ico: "⚙",
    navCatalogId: "automation",
  },
  {
    id: "orchestration",
    hash: "#/orchestration",
    min: "admin",
    group: "Operations",
    label: "Response team",
    ico: "⬡",
    navCatalogAny: ["swarm", "actuators", "external_robots", "external_robot_commands"],
  },
  {
    id: "platform-control",
    hash: "#/platform-control",
    min: "admin",
    group: "Platform",
    label: "Control",
    ico: "⌂",
    navCatalogAny: [
      "platform_control",
      "cross_tenant_share",
      "federation",
      "admin_security",
      "mac_acl_audit",
      "backup_worker",
      "physical_inhibit",
      "adapter_certification",
      "delivery_ledger",
    ],
    saStaffNav: true,
  },

  // -------------------------------------------------------------- Operations / Assets / Admin
  // tip581: Audit Log Center softs (#/events|activity|signals|audit) + aliases purged (was expireTip 580).
  { id: "audit-log", hash: "#/audit-log", min: "user", group: "Administration", label: "Audit Log", ico: "▦" },
  // ZD-DEAD: `#/alerts` registry deleted (expire tip547 soak) — use `#/response?tab=siren`.
  {
    id: "incidents",
    hash: "#/incidents",
    min: "user",
    group: "Operations",
    label: "Incidents",
    ico: "⚡",
    // tip582: #/incidents-legacy alias purged
  },
  {
    id: "snapshots",
    hash: "#/snapshots",
    min: "user",
    group: "Assets",
    label: "Cameras",
    ico: "▤",
    navGate: "camera_snapshot_enabled",
    // tip582: #/city-observation alias purged
  },
  { id: "ota", hash: "#/ota", min: "superadmin", navCap: "sa_ota_admin", group: "Platform", label: "Firmware updates", ico: "↑" },

  {
    id: "response",
    hash: "#/response",
    min: "user",
    group: "Operations",
    label: "Respond",
    ico: "↯",
  },
  { id: "activate", hash: "#/activate", min: "admin", navCap: "sa_device_admin", group: "Assets", label: "Activate device", ico: "+" },

  // -------------------------------------------------------------- Administration deep links (Settings hub)
  { id: "admin", hash: "#/admin", min: "admin", navCap: "sa_user_admin" },
  {
    id: "billing",
    hash: "#/billing",
    min: "admin",
    navCap: "sa_billing_admin",
    navGate: "billing_enabled",
  },
  { id: "telegram", hash: "#/telegram", min: "user" },
  { id: "voice", hash: "#/voice", min: "admin", navGate: "voice_enabled" },
  // Response plans = policy → Settings hub left-nav only (not Operations top-nav duplicate).
  {
    id: "response-plans",
    hash: "#/response-plans",
    min: "admin",
  },
  {
    id: "tenant-delegations",
    hash: "#/tenant-delegations",
    min: "admin",
    navGate: "tenant_acl_enabled",
    navRequiresCap: "can_manage_users",
    hideChildAdmin: true,
  },
  {
    id: "admin-user-scopes",
    hash: "#/admin-user-scopes",
    min: "admin",
    navGate: "tenant_acl_enabled",
    navRequiresCap: "can_manage_users",
    hideChildAdmin: true,
  },
  {
    id: "sa-team",
    hash: "#/sa-team",
    min: "superadmin",
    navGate: "can_provision_sa",
    group: "Platform",
    label: "Staff accounts",
    ico: "SA",
  },
  {
    id: "croc-fde",
    hash: "#/croc-fde",
    min: "superadmin",
    navCap: "sa_user_admin",
    saStaffNav: true,
    group: "Assets",
    label: "CROC-FDE invite",
    ico: "◇",
  },
  {
    id: "fabric",
    hash: "#/fabric",
    min: "superadmin",
    navCap: "sa_device_admin",
    saStaffNav: true,
    group: "Platform",
    label: "Fabric",
    ico: "▦",
  },
]);

/** Display order (top → bottom) of nav groups — CONV-1 final IA. */
const GROUP_ORDER = ["Home", "Operations", "Assets", "Administration", "Platform"];

/** Build NAV_GROUPS from ROUTES — `path` matches the legacy field name so the
 * sidebar renderer in console.raw.js does not need to change. */
export function buildNavGroups() {
  /** @type {Record<string, {title: string, items: any[]}>} */
  const byTitle = {};
  for (const r of ROUTES) {
    if (!r.group || !r.label) continue;
    const g = byTitle[r.group] || (byTitle[r.group] = { title: r.group, items: [] });
    g.items.push({
      id: r.id,
      label: r.label,
      ico: r.ico || "",
      path: r.navPath || r.hash,
      min: r.min || "user",
      navCap: r.navCap,
      navGate: r.navGate,
      navCatalogId: r.navCatalogId,
      navCatalogAny: r.navCatalogAny,
      hideSaStaff: r.hideSaStaff,
      navRequiresCap: r.navRequiresCap,
      hideChildAdmin: r.hideChildAdmin,
      saStaffNav: r.saStaffNav,
      mapNavEligible: r.mapNavEligible,
    });
  }
  return GROUP_ORDER
    .map((t) => byTitle[t])
    .concat(Object.keys(byTitle).filter((t) => !GROUP_ORDER.includes(t)).map((t) => byTitle[t]))
    .filter(Boolean);
}

/** Set of route ids that should be reachable without a signed-in session. */
export const PUBLIC_ROUTE_IDS = Object.freeze(new Set(ROUTES.filter((r) => r.public).map((r) => r.id)));

/** Map of alias hash → canonical route id (e.g. `#/alarm-log` → `signals`). */
export const ROUTE_ALIASES = Object.freeze(
  ROUTES.reduce((acc, r) => {
    if (Array.isArray(r.aliases)) {
      for (const a of r.aliases) acc[String(a)] = r.id;
    }
    return acc;
  }, /** @type {Record<string, string>} */ ({})),
);

/** Map of redirect ``from`` → registry row (ZD-PARTIAL-F18). */
export const ROUTE_REDIRECT_BY_FROM = Object.freeze(
  ROUTE_REDIRECT_REGISTRY.reduce((acc, row) => {
    acc[String(row.from)] = row;
    return acc;
  }, /** @type {Record<string, RedirectSpec>} */ ({})),
);

/**
 * @typedef {Object} RouteSpec
 * @property {string} id           Unique route id (matches `registerRoute("...")`).
 * @property {string} hash         Canonical hash including `#/` prefix.
 * @property {string} [min]        Minimum role: "user" | "admin" | "superadmin".
 * @property {boolean} [public]    Reachable without a session (login flow etc.).
 * @property {string} [group]      Sidebar group title (omit for deep-link routes).
 * @property {string} [label]      Sidebar label (required when `group` is set).
 * @property {string} [ico]        Sidebar glyph.
 * @property {string[]} [aliases]  Alternate hashes that resolve to the same handler — each MUST be in ROUTE_REDIRECT_REGISTRY.
 * @property {string} [navCap]     When set, ``sa_staff`` needs this SA capability (or ``sa_full_access``) to see the nav entry; superadmin/other roles ignore it.
 * @property {boolean} [saStaffNav] When ``min`` is ``superadmin``, ``sa_staff`` sees the nav entry only if ``/auth/me``.ops_dashboard_eligible is true (delegated SA staff).
 * @property {string} [navGate]      Optional boolean field on ``/auth/me`` (e.g. ``can_provision_sa``) — hide when falsy.
 * @property {string} [navRequiresCap]  Dashboard policy key from ``/auth/me.policy``; entry hidden when ``can(key)`` is false.
 * @property {boolean} [hideChildAdmin] When true, hide for child admins (non-empty ``manager_admin`` on ``/auth/me``).
 * @property {boolean} [mapNavEligible] When true, sidebar uses ``canUseOpsMap`` / ``canUseHomeMap`` (role + ``/auth/me.home_map_ui_enabled``).
 * @property {string} [navPath]    Sidebar href when different from ``hash`` (e.g. All devices → ``#/group/_all``).
 */
